import React, { useState, useEffect, useMemo } from 'react';
import { useApi } from '../../api/api';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import './ScallopWeight.css';

const ScallopWeight = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { getBasParameterById } = useApi();
  
  useEffect(() => {
    const fetchScallopWeightData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching scallop weight data for parameter_id 97...');
        const response = await getBasParameterById(97);
        
        console.log('Scallop weight data received:', response.data);
        setData(response.data);
        
      } catch (err) {
        console.error('Error fetching scallop weight data:', err);
        setError(err.message || 'Failed to load scallop weight data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchScallopWeightData();
  }, [getBasParameterById]);
  
  // Process data for Cleveland dot plot
  const chartData = useMemo(() => {
    if (!data?.results) return { processedData: [], categories: [] };
    
    // Group by management area and create chart data
    const areaGroups = {};
    
    // Flatten the nested structure: data.results[].extraction.results[]
    data.results.forEach((paper) => {
      const paperTitle = paper.paper_title;
      const publicationDate = paper.publication_date;
      
      // Check if extraction and results exist
      if (!paper.extraction || !paper.extraction.results) {
        console.warn('No extraction results for paper:', paperTitle);
        return;
      }
      
      // Process each extraction result from this paper
      paper.extraction.results.forEach((result, index) => {
        const area = result.additional_factors?.management_area || 'Unknown';
        const value = parseFloat(result.value);
        
        if (isNaN(value)) {
          console.warn('Invalid value:', result.value);
          return;
        }
        
        if (!areaGroups[area]) {
          areaGroups[area] = [];
        }
        
        areaGroups[area].push({
          id: `${paper.paper_id}-${index}`,
          area: area,
          value: value,
          paper_title: paperTitle,
          publication_date: publicationDate,
          mathematical_form: result.additional_factors?.mathematical_form,
          a_value: result.additional_factors?.a_value,
          b_value: result.additional_factors?.b_value,
          season_month: result.additional_factors?.season_month,
          assumptions_made: result.additional_factors?.assumptions_made,
          year: result.additional_factors?.year,
          raw_value: result.raw_value
        });
      });
    });
    
    console.log('Area groups:', areaGroups);
    
    // Convert to array format for chart
    const categories = Object.keys(areaGroups).sort();
    const processedData = [];
    
    categories.forEach((area, categoryIndex) => {
      areaGroups[area].forEach(point => {
        processedData.push({
          ...point,
          categoryIndex: categoryIndex,
          categoryName: area
        });
      });
    });
    
    console.log('Processed data for chart:', processedData);
    console.log('Categories:', categories);
    
    return { processedData, categories };
  }, [data]);
  
  // Custom tooltip component
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    
    const data = payload[0].payload;
    
    return (
      <div className="custom-tooltip">
        <div className="tooltip-header">
          <strong>{data.area}</strong>
        </div>
        <div className="tooltip-content">
          <div className="tooltip-row">
            <span className="tooltip-label">Weight:</span>
            <span className="tooltip-value">{data.value} g</span>
          </div>
          <div className="tooltip-row">
            <span className="tooltip-label">Paper:</span>
            <span className="tooltip-value">{data.paper_title}</span>
          </div>
          <div className="tooltip-row">
            <span className="tooltip-label">Publication Date:</span>
            <span className="tooltip-value">
              {data.publication_date ? new Date(data.publication_date).toLocaleDateString() : 'N/A'}
            </span>
          </div>
          {data.season_month && (
            <div className="tooltip-row">
              <span className="tooltip-label">Season:</span>
              <span className="tooltip-value">{data.season_month}</span>
            </div>
          )}
          {data.mathematical_form && (
            <div className="tooltip-row">
              <span className="tooltip-label">Formula:</span>
              <span className="tooltip-value">{data.mathematical_form}</span>
            </div>
          )}
          {data.a_value !== undefined && data.a_value !== null && (
            <div className="tooltip-row">
              <span className="tooltip-label">a value:</span>
              <span className="tooltip-value">{data.a_value}</span>
            </div>
          )}
          {data.b_value !== undefined && data.b_value !== null && (
            <div className="tooltip-row">
              <span className="tooltip-label">b value:</span>
              <span className="tooltip-value">{data.b_value}</span>
            </div>
          )}
          {data.assumptions_made && (
            <div className="tooltip-row full-width">
              <span className="tooltip-label">Assumptions:</span>
              <span className="tooltip-value">{data.assumptions_made}</span>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Color palette for different areas
  const getColorForArea = (categoryIndex) => {
    const colors = [
      '#2563eb', '#dc2626', '#16a34a', '#9333ea', '#ea580c',
      '#0891b2', '#ca8a04', '#be123c', '#7c3aed', '#059669'
    ];
    return colors[categoryIndex % colors.length];
  };
  
  if (loading) {
    return (
      <div className="scallop-weight-container">
        <div className="loading-indicator">
          <p>Loading scallop weight data...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="scallop-weight-container">
        <div className="error-message">
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }
  
  if (!data) {
    return (
      <div className="scallop-weight-container">
        <p>No data available</p>
      </div>
    );
  }
  
  return (
    <div className="scallop-weight-container">
      {chartData.processedData.length > 0 && (
        <div className="chart-container">
          <h4>Weight Distribution by Management Area</h4>
            <p className="chart-description">
            This chart displays predicted scallop meat weights from various length-weight relationship models 
            across different management areas. Each data point represents a model output with standardized 
            parameters (typically 110mm shell height and 50m depth, when depth is considered in the model). 
            Hover over any point to view the specific model parameters, publication details, and assumptions.
            </p>
          <ResponsiveContainer width="100%" height={Math.max(500, chartData.categories.length * 80)}>
            <ScatterChart
              margin={{ top: 20, right: 40, bottom: 40, left: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                type="number" 
                dataKey="value" 
                name="Weight"
                label={{ value: 'Weight (g)', position: 'insideBottom', offset: -15 }}
                domain={['auto', 'auto']}
              />
              <YAxis 
                type="number"
                dataKey="categoryIndex"
                name="Management Area"
                ticks={chartData.categories.map((_, i) => i)}
                tickFormatter={(value) => chartData.categories[value]}
                domain={[0, chartData.categories.length - 1]}
                width={200}
              />
              <Tooltip 
                content={<CustomTooltip />}
                isAnimationActive={false}
              />
              <Scatter 
                data={chartData.processedData} 
                fill="#8884d8"
                isAnimationActive={false}
                shape="circle"
              >
                {chartData.processedData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={getColorForArea(entry.categoryIndex)}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )}
      
      <div className="debug-info" style={{ marginTop: '20px', padding: '10px', background: '#f5f5f5', borderRadius: '4px' }}>
        <details>
          <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Raw Data (click to expand)</summary>
          <pre style={{ fontSize: '10px', overflow: 'auto', maxHeight: '300px' }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        </details>
      </div>
    </div>
  );
};

export default ScallopWeight;